import React, { useContext, useEffect } from 'react';

import * as S from './styles';
import Modal from 'components/Modal';
import SimpleInput from 'components/SimpleInput';
import SimpleTextArea from 'components/SimpleTextArea';
import SelectInput from 'components/SelectInput';
import InputImgBigger from 'components/InputImgBigger';
import Carousel from 'components/Carousel';
import SystemNotifyContext from 'context/SystemNotifyContext';
import LoadGeneralContext from 'context/LoadGeneralContext';
import { ImgSavedProps } from 'types/ImgSavedProps';
import {
	BannerPopupCountdownImageProps,
	BannerPopupProps,
	BannerPopupRedirectType,
	BannerPopupStatus,
	BannerPopupType
} from 'types/BannerPopupProps';
import { SelectInputItemProps } from 'types/SelectInputItemProps';
import {
	addBannerPopupReq,
	editBannerPopupReq,
	getBannerPopupCategories,
	isBannerPopupInsideValidity
} from 'pages/BannerPopup/reqs';

interface FormBannerPopupProps {
	isOpenForm: boolean;
	setIsOpenForm: React.Dispatch<React.SetStateAction<boolean>>;
	bannerToEdit: BannerPopupProps | null;
	setBannerToEdit: React.Dispatch<
		React.SetStateAction<BannerPopupProps | null>
	>;
	setListBanners: React.Dispatch<React.SetStateAction<BannerPopupProps[]>>;
	listBanners: BannerPopupProps[];
}

const typeOptions: SelectInputItemProps[] = [
	{ id: 'COUNTDOWN', option: 'PopUp com Contador' },
	{ id: 'SIMPLE', option: 'PopUp Simples' }
];

const statusOptions: SelectInputItemProps[] = [
	{ id: 'ACTIVE', option: 'Ativo' },
	{ id: 'INACTIVE', option: 'Inativo' }
];

const redirectTypeOptions: SelectInputItemProps[] = [
	{ id: 'COLLECTION', option: 'Colecao' },
	{ id: 'CATEGORY', option: 'Categoria' }
];

const initialCountdownImage: BannerPopupCountdownImageProps = {
	id: '',
	imageUrl: '',
	redirectType: 'COLLECTION',
	redirectValue: '',
	redirectLabel: ''
};

const initialStateForm: BannerPopupProps = {
	id: '',
	name: '',
	rne: '',
	type: 'COUNTDOWN',
	title: '',
	description: '',
	imageUrl: '',
	countdownImages: [],
	redirectUrl: '',
	ctas: [
		{ label: '', redirectUrl: '' },
		{ label: '', redirectUrl: '' }
	],
	startDate: '',
	endDate: '',
	status: 'INACTIVE',
	distributorIds: [],
	cluster: '',
	createdAt: '',
	updatedAt: ''
};

function splitValues(value: string) {
	return value
		.split(',')
		.map((item) => item.trim())
		.filter(Boolean);
}

function joinValues(value?: string[]) {
	return value?.join(', ') || '';
}

function findOption(value: string, list: SelectInputItemProps[]) {
	return list.find((item) => item.id === value) || '';
}

function normalizeDateTimeLocal(value: string, fallbackTime: string) {
	if (!value) {
		return '';
	}

	if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
		return `${value}T${fallbackTime}`;
	}

	const normalizedValue = value.replace(' ', 'T');

	if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(normalizedValue)) {
		return normalizedValue.slice(0, 16);
	}

	return '';
}

function normalizeForm(banner: BannerPopupProps): BannerPopupProps {
	const legacyType = banner.type as string;

	return {
		...banner,
		type:
			legacyType === 'EVENT'
				? 'COUNTDOWN'
				: legacyType === 'OFFER'
					? 'SIMPLE'
					: banner.type,
		imageUrl:
			banner.imageUrl || banner.desktopImageUrl || banner.mobileImageUrl || '',
		rne: banner.rne || '',
		cluster: banner.cluster || '',
		countdownImages: banner.countdownImages || [],
		ctas:
			banner.ctas && banner.ctas.length > 0
				? [
						banner.ctas[0] || { label: '', redirectUrl: '' },
						banner.ctas[1] || { label: '', redirectUrl: '' }
					]
				: [
						{
							label: banner.ctaLabel || '',
							redirectUrl: banner.redirectUrl || ''
						},
						{ label: '', redirectUrl: '' }
					],
		distributorIds: banner.distributorIds || [],
		startDate: normalizeDateTimeLocal(banner.startDate, '00:00'),
		endDate: normalizeDateTimeLocal(banner.endDate, '23:59')
	};
}

function getDaysToEnd(endDate: string) {
	if (endDate === '') {
		return { days: '00', hours: '00', minutes: '00', seconds: '00' };
	}

	const endDateValue = endDate.includes('T') ? endDate : `${endDate}T23:59:59`;
	const diff = new Date(endDateValue).getTime() - new Date().getTime();
	const safeDiff = diff > 0 ? diff : 0;
	const days = Math.floor(safeDiff / (1000 * 60 * 60 * 24));
	const hours = Math.floor((safeDiff / (1000 * 60 * 60)) % 24);
	const minutes = Math.floor((safeDiff / (1000 * 60)) % 60);
	const seconds = Math.floor((safeDiff / 1000) % 60);

	return {
		days: String(days).padStart(2, '0'),
		hours: String(hours).padStart(2, '0'),
		minutes: String(minutes).padStart(2, '0'),
		seconds: String(seconds).padStart(2, '0')
	};
}

export default function FormBannerPopup({
	isOpenForm,
	setIsOpenForm,
	bannerToEdit,
	setBannerToEdit,
	setListBanners,
	listBanners
}: FormBannerPopupProps) {
	const { handleNotify } = useContext(SystemNotifyContext);
	const { setIsOpenLoad, setMessage } = useContext(LoadGeneralContext);

	const [dataForm, setDataForm] = React.useState<BannerPopupProps>(
		bannerToEdit ? normalizeForm(bannerToEdit) : { ...initialStateForm }
	);
	const [distributorIdsText, setDistributorIdsText] = React.useState(
		joinValues(dataForm.distributorIds)
	);
	const [imageDraft, setImageDraft] =
		React.useState<BannerPopupCountdownImageProps>({
			...initialCountdownImage
		});
	const [imageToEdit, setImageToEdit] =
		React.useState<BannerPopupCountdownImageProps | null>(null);
	const [isOpenImageForm, setIsOpenImageForm] = React.useState(false);
	const [categoryOptions, setCategoryOptions] = React.useState<
		SelectInputItemProps[]
	>([]);
	const [, setCounterTick] = React.useState(0);

	const counter = getDaysToEnd(dataForm.endDate);
	const isSimplePopup = dataForm.type === 'SIMPLE';

	useEffect(() => {
		if (!isOpenForm || dataForm.type !== 'COUNTDOWN') {
			return undefined;
		}

		const interval = setInterval(() => {
			setCounterTick((value) => value + 1);
		}, 1000);

		return () => clearInterval(interval);
	}, [dataForm.type, isOpenForm]);

	useEffect(() => {
		if (
			dataForm.type !== 'COUNTDOWN' ||
			imageDraft.redirectType !== 'CATEGORY' ||
			categoryOptions.length > 0
		) {
			return;
		}

		(async () => {
			const { isSuccess, list } = await getBannerPopupCategories(handleNotify);
			if (isSuccess) {
				setCategoryOptions(list);
			}
		})();
	}, [
		categoryOptions.length,
		dataForm.type,
		handleNotify,
		imageDraft.redirectType
	]);

	function resetStatesComp() {
		setIsOpenForm(false);
		setDataForm(initialStateForm);
		setBannerToEdit(null);
	}

	function updateCta(
		index: number,
		field: 'label' | 'redirectUrl',
		value: string
	) {
		setDataForm((obj) => {
			const ctas = [...obj.ctas];
			ctas[index] = {
				...(ctas[index] || { label: '', redirectUrl: '' }),
				[field]: value
			};
			return { ...obj, ctas };
		});
	}

	function openImageForm(image?: BannerPopupCountdownImageProps) {
		setImageToEdit(image || null);
		setImageDraft(image || { ...initialCountdownImage });
		setIsOpenImageForm(true);
	}

	function closeImageForm() {
		setIsOpenImageForm(false);
		setImageToEdit(null);
		setImageDraft({ ...initialCountdownImage });
	}

	function saveCountdownImage() {
		if (imageDraft.imageUrl === '') {
			handleNotify('Insira a imagem do PopUp com Contador.', 'error');
			return;
		}

		if (imageDraft.redirectValue === '') {
			handleNotify('Preencha o redirecionamento da imagem.', 'error');
			return;
		}

		const imageToSave = {
			...imageDraft,
			id: imageToEdit?.id || `countdown-image-${Date.now()}`,
			redirectLabel: imageDraft.redirectLabel || imageDraft.redirectValue
		};

		setDataForm((obj) => {
			if (imageToEdit) {
				return {
					...obj,
					countdownImages: obj.countdownImages.map((image) =>
						image.id === imageToEdit.id ? imageToSave : image
					)
				};
			}

			return {
				...obj,
				countdownImages: [...obj.countdownImages, imageToSave]
			};
		});
		closeImageForm();
	}

	function hasAnotherActivePopup() {
		return listBanners.some(
			(item) =>
				item.status === 'ACTIVE' &&
				isBannerPopupInsideValidity(item) &&
				item.id !== dataForm.id
		);
	}

	function verifyIfCanSave() {
		if (dataForm.name === '') {
			handleNotify('Preencha o nome interno da campanha.', 'error');
			return false;
		}

		if (dataForm.title === '') {
			handleNotify('Preencha o titulo.', 'error');
			return false;
		}

		if ((dataForm.description || '') === '') {
			handleNotify('Preencha a descricao.', 'error');
			return false;
		}

		if (isSimplePopup && dataForm.imageUrl === '') {
			handleNotify('Insira a imagem do PopUp Simples.', 'error');
			return false;
		}

		if (!isSimplePopup && dataForm.countdownImages.length === 0) {
			handleNotify(
				'Cadastre pelo menos uma imagem no PopUp com Contador.',
				'error'
			);
			return false;
		}

		const fallbackRedirectUrl = dataForm.ctas[0]?.redirectUrl || '';

		if (dataForm.redirectUrl === '' && fallbackRedirectUrl === '') {
			handleNotify('Preencha o link de redirecionamento.', 'error');
			return false;
		}

		if (dataForm.startDate === '') {
			handleNotify('Preencha a data de inicio.', 'error');
			return false;
		}

		if (dataForm.endDate === '') {
			handleNotify('Preencha a data de termino.', 'error');
			return false;
		}

		if (new Date(dataForm.endDate) < new Date(dataForm.startDate)) {
			handleNotify(
				'A data de termino nao pode ser menor que a inicial.',
				'error'
			);
			return false;
		}

		if (dataForm.status === 'ACTIVE' && hasAnotherActivePopup()) {
			handleNotify('Ja existe outro Banner PopUp ativo.', 'error');
			return false;
		}

		return true;
	}

	function getFormToSave() {
		const cleanCtas = dataForm.ctas.filter(
			(cta) => cta.label !== '' || cta.redirectUrl !== ''
		);
		const fallbackRedirectUrl = dataForm.ctas[0]?.redirectUrl || '';

		return {
			...dataForm,
			redirectUrl: dataForm.redirectUrl || fallbackRedirectUrl,
			ctas: cleanCtas,
			countdownImages: isSimplePopup ? [] : dataForm.countdownImages,
			imageUrl: isSimplePopup ? dataForm.imageUrl : ''
		};
	}

	async function saveBanner() {
		if (!verifyIfCanSave()) {
			return;
		}

		const { isSuccess, bannerSaved } = await addBannerPopupReq(
			getFormToSave(),
			setIsOpenLoad,
			setMessage,
			handleNotify
		);

		if (isSuccess && bannerSaved) {
			setListBanners((list) => [...list, bannerSaved]);
			resetStatesComp();
		}
	}

	async function editBanner() {
		if (!verifyIfCanSave() || !bannerToEdit) {
			return;
		}

		const formToSave = getFormToSave();
		const { isSuccess } = await editBannerPopupReq(
			bannerToEdit,
			formToSave,
			setIsOpenLoad,
			setMessage,
			handleNotify
		);

		if (isSuccess) {
			setListBanners((list) =>
				list.map((item) =>
					item.id === bannerToEdit.id
						? { ...formToSave, updatedAt: new Date().toISOString() }
						: item
				)
			);
			resetStatesComp();
		}
	}

	return (
		<Modal
			isOpenProps={isOpenForm}
			setIsOpenModal={() => {
				setIsOpenForm((value) => !value);
				resetStatesComp();
			}}
			title="Formulario Banner PopUp"
			buttonClose={false}
		>
			<S.container>
				<S.content>
					<S.contentForm>
						<SelectInput
							id="banner-popup-status"
							list={statusOptions}
							initialValue={findOption(dataForm.status, statusOptions)}
							label="Status"
							getValue={(value: SelectInputItemProps | '') =>
								value !== '' &&
								setDataForm((obj) => ({
									...obj,
									status: value.id as BannerPopupStatus
								}))
							}
						/>

						<S.twoInputs>
							<SimpleInput
								id="banner-popup-name"
								label="Nome interno"
								initialValue={dataForm.name}
								getValue={(value: string) =>
									setDataForm((obj) => ({ ...obj, name: value }))
								}
							/>
							<SelectInput
								diseable
								id="banner-popup-type"
								list={typeOptions}
								initialValue={findOption(dataForm.type, typeOptions)}
								label="Tipo do pop-up"
								getValue={(value: SelectInputItemProps | '') =>
									value !== '' &&
									setDataForm((obj) => ({
										...obj,
										type: value.id as BannerPopupType
									}))
								}
							/>
						</S.twoInputs>

						<SimpleInput
							id="banner-popup-title"
							label="Titulo"
							initialValue={dataForm.title}
							getValue={(value: string) =>
								setDataForm((obj) => ({ ...obj, title: value }))
							}
						/>

						<SimpleTextArea
							id="banner-popup-description"
							borderColor="#CED4DA"
							labelColor="#6C757D"
							colorFont="#6C757D"
							rows={5}
							title="Descricao"
							textArea={dataForm.description || ''}
							getValue={(value: string) =>
								setDataForm((obj) => ({ ...obj, description: value }))
							}
							showCountString={false}
						/>

						{isSimplePopup ? (
							<InputImgBigger
								idImagem="bannerPopupSimpleImage"
								initialImage={dataForm.imageUrl}
								getImage={(value: ImgSavedProps) =>
									setDataForm((obj) => ({
										...obj,
										imageUrl: value?.url || ''
									}))
								}
								label="Imagem"
								imgHeight={160}
							/>
						) : (
							<S.galleryForm>
								<S.addImageButton type="button" onClick={() => openImageForm()}>
									Adicionar imagem
								</S.addImageButton>
								<S.imageCounter>
									{dataForm.countdownImages.length} imagem(ns) cadastrada(s)
								</S.imageCounter>
								{dataForm.countdownImages.length > 0 && (
									<S.registeredImages>
										{dataForm.countdownImages.map((item) => (
											<S.registeredImage key={item.id}>
												<img src={item.imageUrl} alt={item.redirectLabel} />
												<p>{item.redirectLabel}</p>
												<S.registeredImageActions>
													<button
														type="button"
														onClick={() => openImageForm(item)}
													>
														Editar
													</button>
													<button
														type="button"
														onClick={() =>
															setDataForm((obj) => ({
																...obj,
																countdownImages: obj.countdownImages.filter(
																	(image) => image.id !== item.id
																)
															}))
														}
													>
														Remover
													</button>
												</S.registeredImageActions>
											</S.registeredImage>
										))}
									</S.registeredImages>
								)}
							</S.galleryForm>
						)}

						<SimpleInput
							id="banner-popup-redirect-url"
							label="Link de redirecionamento"
							initialValue={dataForm.redirectUrl}
							getValue={(value: string) =>
								setDataForm((obj) => ({ ...obj, redirectUrl: value }))
							}
						/>

						<S.twoInputs>
							<SimpleInput
								id="banner-popup-cta-1-label"
								label="Texto do CTA 1"
								initialValue={dataForm.ctas[0]?.label || ''}
								getValue={(value: string) => updateCta(0, 'label', value)}
							/>
							<SimpleInput
								id="banner-popup-cta-1-url"
								label="Link do CTA 1"
								initialValue={dataForm.ctas[0]?.redirectUrl || ''}
								getValue={(value: string) => updateCta(0, 'redirectUrl', value)}
							/>
						</S.twoInputs>

						<S.twoInputs>
							<SimpleInput
								id="banner-popup-cta-2-label"
								label="Texto do CTA 2"
								initialValue={dataForm.ctas[1]?.label || ''}
								getValue={(value: string) => updateCta(1, 'label', value)}
							/>
							<SimpleInput
								id="banner-popup-cta-2-url"
								label="Link do CTA 2"
								initialValue={dataForm.ctas[1]?.redirectUrl || ''}
								getValue={(value: string) => updateCta(1, 'redirectUrl', value)}
							/>
						</S.twoInputs>

						<S.twoInputs>
							<SimpleInput
								id="banner-popup-start-date"
								paddingInLineEnd
								label="Data de inicio"
								typeInput="datetime-local"
								initialValue={dataForm.startDate}
								getValue={(value: string) =>
									setDataForm((obj) => ({ ...obj, startDate: value }))
								}
							/>
							<SimpleInput
								id="banner-popup-end-date"
								paddingInLineEnd
								label="Data de termino"
								typeInput="datetime-local"
								initialValue={dataForm.endDate}
								getValue={(value: string) =>
									setDataForm((obj) => ({ ...obj, endDate: value }))
								}
							/>
						</S.twoInputs>

						<S.sectionTitle>Segmentacao</S.sectionTitle>
						<S.twoInputs>
							<SimpleInput
								id="banner-popup-distributors"
								label="Distribuidores"
								placeHolder="Separe os IDs por virgula"
								initialValue={distributorIdsText}
								getValue={(value: string) => {
									setDistributorIdsText(value);
									setDataForm((obj) => ({
										...obj,
										distributorIds: splitValues(value)
									}));
								}}
							/>
							<SimpleInput
								id="banner-popup-rne"
								label="RNE"
								initialValue={dataForm.rne || ''}
								getValue={(value: string) =>
									setDataForm((obj) => ({ ...obj, rne: value }))
								}
							/>
						</S.twoInputs>
						<S.twoInputs>
							<SimpleInput
								id="banner-popup-cluster"
								label="Cluster"
								initialValue={dataForm.cluster || ''}
								getValue={(value: string) =>
									setDataForm((obj) => ({ ...obj, cluster: value }))
								}
							/>
						</S.twoInputs>
					</S.contentForm>

					<S.preview>
						<S.previewTitle>Preview</S.previewTitle>
						<S.popupPreview $variant={dataForm.type}>
							<S.popupCard $variant={dataForm.type}>
								<S.previewClose>×</S.previewClose>
								{isSimplePopup ? (
									<>
										<S.simpleTitle>
											{dataForm.title || 'Titulo do pop-up'}
										</S.simpleTitle>
										<S.previewImage $variant={dataForm.type}>
											{dataForm.imageUrl !== '' && (
												<img src={dataForm.imageUrl} alt={dataForm.title} />
											)}
										</S.previewImage>
									</>
								) : (
									<>
										<S.countdownTitle>
											{dataForm.title || 'Snack Friday'}
										</S.countdownTitle>
										<S.previewCarousel>
											<Carousel
												items={dataForm.countdownImages.map((item) => ({
													id: item.id,
													imageUrl: item.imageUrl
												}))}
												isAutoPlay
												interval={2500}
												isBtns={false}
												showClicks={false}
												positionContainerBalls={0}
											/>
										</S.previewCarousel>
									</>
								)}
								<S.previewText $variant={dataForm.type}>
									<h3>
										{dataForm.description ||
											(isSimplePopup
												? 'Abasteca seu estoque e transforme sentimento em resultados'
												: 'Aproveite ate 50% de desconto na Black Friday Mondelez!')}
									</h3>
									{dataForm.type === 'COUNTDOWN' && (
										<S.counter>
											<span>
												{counter.days}
												<small>DIAS</small>
											</span>
											<span>
												{counter.hours}
												<small>HORAS</small>
											</span>
											<span>
												{counter.minutes}
												<small>MINUTOS</small>
											</span>
											<span>
												{counter.seconds}
												<small>SEGUNDOS</small>
											</span>
										</S.counter>
									)}
									{dataForm.ctas[0]?.label && (
										<S.cta $variant={dataForm.type}>
											{dataForm.ctas[0].label}
										</S.cta>
									)}
									{dataForm.ctas[1]?.label && (
										<S.secondaryCta $variant={dataForm.type}>
											{dataForm.ctas[1].label}
										</S.secondaryCta>
									)}
								</S.previewText>
							</S.popupCard>
						</S.popupPreview>
					</S.preview>
				</S.content>

				<S.containerBtns>
					<S.cancel onClick={resetStatesComp}>Cancelar</S.cancel>
					<S.save
						onClick={async () => {
							if (bannerToEdit) {
								await editBanner();
							} else {
								await saveBanner();
							}
						}}
					>
						Salvar
					</S.save>
				</S.containerBtns>
			</S.container>

			<Modal
				isOpenProps={isOpenImageForm}
				setIsOpenModal={closeImageForm}
				title={imageToEdit ? 'Editar imagem' : 'Adicionar imagem'}
				buttonClose={false}
			>
				<S.imageModalContent>
					<InputImgBigger
						idImagem="bannerPopupCountdownImage"
						initialImage={imageDraft.imageUrl}
						getImage={(value: ImgSavedProps) =>
							setImageDraft((obj) => ({
								...obj,
								imageUrl: value?.url || ''
							}))
						}
						label="Imagem do contador"
						imgHeight={220}
					/>
					<S.twoInputs>
						<SelectInput
							id="banner-popup-image-redirect-type"
							list={redirectTypeOptions}
							initialValue={findOption(
								imageDraft.redirectType,
								redirectTypeOptions
							)}
							label="Tipo de redirecionamento"
							dropdownPlacement="top"
							getValue={(value: SelectInputItemProps | '') =>
								value !== '' &&
								setImageDraft((obj) => {
									const redirectType = value.id as BannerPopupRedirectType;

									if (obj.redirectType === redirectType) {
										return obj;
									}

									return {
										...obj,
										redirectType,
										redirectValue: '',
										redirectLabel: ''
									};
								})
							}
						/>
						{imageDraft.redirectType === 'CATEGORY' ? (
							<SelectInput
								id="banner-popup-image-category"
								list={categoryOptions}
								initialValue={findOption(
									imageDraft.redirectValue,
									categoryOptions
								)}
								label="Categoria"
								dropdownPlacement="top"
								getValue={(value: SelectInputItemProps | '') =>
									value !== '' &&
									setImageDraft((obj) => ({
										...obj,
										redirectValue: String(value.id),
										redirectLabel: value.option
									}))
								}
							/>
						) : (
							<SimpleInput
								id="banner-popup-image-collection"
								label="Colecao"
								placeHolder="ID ou slug da colecao"
								initialValue={imageDraft.redirectValue}
								getValue={(value: string) =>
									setImageDraft((obj) => ({
										...obj,
										redirectValue: value,
										redirectLabel: value
									}))
								}
							/>
						)}
					</S.twoInputs>
					<S.containerBtns>
						<S.cancel type="button" onClick={closeImageForm}>
							Cancelar
						</S.cancel>
						<S.save type="button" onClick={saveCountdownImage}>
							Salvar imagem
						</S.save>
					</S.containerBtns>
				</S.imageModalContent>
			</Modal>
		</Modal>
	);
}
